import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { btn } from "@/components/ds/styles";
import { OrderTermsNote } from "@/components/payments/order-terms-note";
import { formatUsdShort } from "@/lib/payments/currency";
import { DS_INPUT } from "@/components/ds/field";
import { SMART_PILLS_AR } from "@/data/bousala-landing";
import { SMART_SECTION_KEYS } from "@/lib/bousala/smart-order";
import { queryOptions, useSuspenseQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { userErrorMessage } from "@/lib/user-error";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { canonicalLink, canonicalMeta } from "@/lib/seo";
import {
  getBousalaBuyContext,
  submitSmartReceiptPurchase,
  getBousalaReceiptUploadUrl,
} from "@/lib/bousala-purchase.functions";
import { getSmartPortalState, getSmartUiProfile } from "@/lib/bousala/smart-portal.functions";
import { smartEntryStep } from "@/lib/bousala/smart-entry-step";
import { ensureAnonSession } from "@/lib/auth/anon-session";
import { PhoneField, prefillPhone, toE164, useDial, phoneErrorMessage } from "@/components/common/phone-field";
import { PaypalReceiptForm } from "@/components/payments/paypal-receipt-form";
import { InsideSyriaPaymentInstructions, PaymentNotice, PaymentRegionSelector, PaymentStepHeading } from "@/components/payments/payment-flow";
import { useSmartAdminPreview, SmartPreviewBanner } from "@/components/bousala/smart/smart-preview";
import { getSmartAdminPreview } from "@/lib/bousala/smart-preview.functions";
import { SmartLanguageToggle, useSmartUiLanguage } from "@/components/bousala/smart/smart-language-toggle";
import { SMART_CHECKOUT_COPY } from "@/lib/bousala/smart-buyer-copy";
import { completeSmartReceiptSubmission } from "@/lib/bousala/smart-receipt-completion";
import type { Lang } from "@/lib/bousala/i18n";
import { formatSypPrice } from "@/lib/payments/syp";


const ctxQ = () =>
  queryOptions({
    queryKey: ["bousala", "buy-context", "smart"],
    queryFn: () => getBousalaBuyContext({ data: { product: "smart" } }),
  });

export const Route = createFileRoute("/_public/bousala/smart")({
  // Checkout step only: before launch there is nothing to buy here, so the
  // visitor belongs on the landing page instead.
  loader: async ({ context }) => {
    const ctx = await context.queryClient.ensureQueryData(ctxQ());
    if (!(ctx as any)?.product) {
      // SSR has no session (Supabase keeps it in localStorage), so anonymous
      // server rendering keeps redirecting exactly as before. In the browser
      // an admin is asked — the server verifies the role — and stays here.
      if (typeof window === "undefined") throw redirect({ to: "/bousala" });
      const preview = await context.queryClient.ensureQueryData({
        queryKey: ["bousala", "smart-admin-preview"],
        queryFn: () => getSmartAdminPreview({ data: { product: "smart" } }),
        staleTime: 60_000,
      });
      if (!(preview as any)?.product) throw redirect({ to: "/bousala" });
    }
    return ctx;
  },
  head: () => ({
    meta: [
      { title: "طلب البوصلة الذكية — د. سارة النحّاس" },
      { name: "description", content: "البوصلة الذكية بـ 10$: تقرير مفصّل مبني على نتيجة البوصلة ونموذج التفضيلات — التخصصات الأنسب، مقارنة الخيارات، التحديات، وخطة 30 يوم. يوصل خلال 24 ساعة." },
      { name: "robots", content: "noindex, nofollow" },
      ...canonicalMeta("/bousala"),
    ],
    links: [canonicalLink("/bousala")],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: SmartPage,
});

function useSession() {
  const [session, setSession] = useState<{ ready: boolean; userId: string | null; email: string | null }>({
    ready: false,
    userId: null,
    email: null,
  });
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) =>
      setSession({ ready: true, userId: data.session?.user.id ?? null, email: data.session?.user.email ?? null }),
    );
    const { data } = supabase.auth.onAuthStateChange((_e, s) =>
      setSession({ ready: true, userId: s?.user.id ?? null, email: s?.user.email ?? null }),
    );
    return () => data.subscription.unsubscribe();
  }, []);
  return session;
}

function SmartPage() {
  const { data } = useSuspenseQuery(ctxQ());
  const { product, isPreview } = useSmartAdminPreview((data as any).product ?? null);
  // Price/title come from the catalog row even before launch, so visitors can
  // see what the report costs while the CTA stays disabled.
  const catalog = (data as any).catalog ?? product;
  const priceUsd = catalog ? Number(catalog.price_usd) : null;
  const session = useSession();
  const profile = useQuery({
    queryKey: ["smart", "ui-profile", session.userId],
    enabled: Boolean(session.userId),
    queryFn: () => getSmartUiProfile(),
    staleTime: 60_000,
  });
  const [lang, setLang] = useSmartUiLanguage(profile.data?.preferredLanguage);
  const [country, setCountry] = useState<"outside" | "SY">("outside");
  const copy = SMART_CHECKOUT_COPY[lang];

  const en = lang === "en";
  const offer = en
    ? null
    : [
        `${SMART_SECTION_KEYS.length} أقسام مكتوبة على نتيجة البوصلة ونموذج التفضيلات`,
        "مراجعة من فريق د. سارة قبل التسليم",
        "التقرير بالعربي أو بالإنجليزي",
      ];
  return (
    <div dir={en ? "ltr" : "rtl"} className="bg-cream-200">
      <div className="mx-auto max-w-[44rem] px-5 py-10 md:py-14">
        <div className="flex items-center justify-between gap-3">
          <Link to="/bousala" search={{ lang }} hash="smart" className="inline-flex min-h-12 items-center font-display text-[15px] font-bold text-brown-500 hover:text-brown-700">
            {copy.back}
          </Link>
          <SmartLanguageToggle lang={lang} onChange={setLang} />
        </div>
        <div className="mt-4"><SmartPreviewBanner show={isPreview} lang={lang} /></div>
        <header className="mt-6">
          <p className="font-display text-[14px] font-bold text-amber-800">{copy.smartName}</p>
          <h1 className="mt-3 font-display text-[34px] font-extrabold leading-[1.3] text-brown-700 md:text-[42px]">
            {en ? copy.smartName : (<>تقرير مفصّل مبني على <mark className="mark bg-transparent text-inherit">نتيجتك</mark></>)}
          </h1>
          <p className="mt-4 text-[19px] leading-[1.8] text-brown-700">
            {en ? copy.descriptionFallback : catalog?.description_ar ?? copy.descriptionFallback}
          </p>
          {!en ? (
            <ul className="mt-5 flex flex-wrap gap-2">
              {SMART_PILLS_AR.map((p) => (
                <li key={p} className="rounded-ds-pill bg-surface px-4 py-2 font-display text-[14px] font-bold text-brown-700">{p}</li>
              ))}
            </ul>
          ) : null}
          {priceUsd != null ? (
            <p className="mt-6 font-display text-[44px] font-extrabold leading-none text-amber-800" dir="ltr" style={{ textAlign: en ? "left" : "right" }}>
              {country === "SY" ? `${formatSypPrice(priceUsd)} ${copy.sypCurrency}` : formatUsdShort(priceUsd)}
            </p>
          ) : null}
        </header>

        {offer ? (
          <section className="mt-8 rounded-ds-2xl bg-amber-100 px-6 py-7">
            <ul className="divide-y divide-amber-800/10">
              {offer.map((o) => (
                <li key={o} className="flex items-center gap-3 py-3 text-[17px] text-brown-700">
                  <Check className="size-5 shrink-0 text-amber-800" strokeWidth={1.75} aria-hidden />
                  {o}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section id="checkout" className="mt-8 scroll-mt-24">
          <SmartCta
            product={product}
            priceUsd={priceUsd}
            inside={data.inside_syria}
            paypal={(data as any).paypal_outside_syria}
            lang={lang}
            country={country}
            onCountryChange={setCountry}
          />
        </section>
      </div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-ds-xl border border-hairline-subtle bg-surface p-6 shadow-sm md:p-8">{children}</div>;
}

function SmartCta({
  product,
  priceUsd,
  inside,
  paypal,
  lang,
  country,
  onCountryChange,
}: {
  product: any;
  priceUsd: number | null;
  inside: any;
  paypal: any;
  lang: Lang;
  country: "outside" | "SY";
  onCountryChange: (v: "outside" | "SY") => void;
}) {
  const copy = SMART_CHECKOUT_COPY[lang];
  const { ready, userId, email } = useSession();

  // Recognises a pending receipt too (no smart_reports row yet), so a buyer
  // under review can never submit a second one.
  const portal = useQuery({
    queryKey: ["smart", "portal"],
    enabled: Boolean(userId),
    queryFn: () => getSmartPortalState(),
    staleTime: 30_000,
    retry: false,
  });

  // The loader redirects to /bousala before launch, so `product` is set here.
  if (!product) return null;

  if (!ready) {
    return (
      <Card>
         <div className="space-y-3" aria-label={copy.loading} role="status"><div className="h-5 w-1/2 animate-pulse rounded bg-cream-200" /><div className="h-12 animate-pulse rounded-ds-md bg-cream-100" /></div>
      </Card>
    );
  }

  const step = smartEntryStep({
    session: userId ? (email ? "email" : "anonymous") : "none",
    portalKind: (portal.data?.kind as string | undefined) ?? null,
    portalLoading: Boolean(userId) && portal.isLoading,
  });

  if (step === "loading") {
    return (
      <Card>
        <div className="space-y-3" aria-label={copy.loading} role="status"><div className="h-5 w-1/2 animate-pulse rounded bg-cream-200" /><div className="h-12 animate-pulse rounded-ds-md bg-cream-100" /></div>
      </Card>
    );
  }

  if (step === "order_status") {
    return (
      <Card>
        <p className="font-display text-[23px] font-extrabold text-brown-700">{copy.orderRegistered}</p>
        <p className="mt-2 text-[17px] leading-[1.85] text-brown-700">
          {copy.orderRegisteredBody}
        </p>
        <a
          href={`/portal/smart?lang=${lang}`}
          className={"mt-5 " + `${btn.primary} min-h-14 w-full disabled:opacity-60`}
        >
          {copy.openSmart}
        </a>
      </Card>
    );
  }

  if (step === "add_email") return <AddEmailStep needsAnonSession={!userId} lang={lang} />;

  // Checkout — whatever the Bousala 1 attempt status is. The buyer pays first
  // and takes the free test while the transfer is under review.
  return (
    <SmartCheckout
      email={email ?? ""}
      priceUsd={priceUsd ?? 0}
      inside={inside}
      paypal={paypal}
      lang={lang}
      country={country}
      onCountryChange={onCountryChange}
    />
  );
}

function SmartCheckout({
  email,
  priceUsd,
  inside,
  paypal,
  lang,
  country,
  onCountryChange,
}: {
  email: string;
  priceUsd: number;
  inside: any;
  paypal: any;
  lang: Lang;
  country: "outside" | "SY";
  onCountryChange: (v: "outside" | "SY") => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const getUploadUrl = useServerFn(getBousalaReceiptUploadUrl);
  const submitLocal = useServerFn(submitSmartReceiptPurchase);
  const copy = SMART_CHECKOUT_COPY[lang];
  const finishReceipt = () =>
    completeSmartReceiptSubmission(
      queryClient,
      (nextLang) => navigate({ to: "/portal/smart", search: { lang: nextLang } as any }),
      lang,
    );
  return (
    <>
      <PaymentRegionSelector
        value={country}
        onChange={onCountryChange}
        outsideLabel={copy.outsideSyria}
        insideLabel={copy.insideSyria}
        lang={lang}
      />

      {country === "SY" ? (
        <PaymentNotice>{copy.insideSyriaNote}</PaymentNotice>
      ) : null}

      {country === "outside" ? (
        <PaypalReceiptForm
          idPrefix="smart-paypal"
          priceUsd={priceUsd}
          instructions={paypal}
          lockedEmail={email}
          lang={lang}
          getUploadUrl={(filename) => getUploadUrl({ data: { filename } })}
          footerNote={copy.receiptFooter}
          submit={(payload) => submitLocal({ data: { ...payload, lang } })}
          onDone={finishReceipt}
        />
      ) : (
        <SmartLocalForm email={email} insideInstructions={inside} lang={lang} onDone={finishReceipt} />
      )}
    </>
  );
}

function LockedEmail({ email, lang }: { email: string; lang: Lang }) {
  const copy = SMART_CHECKOUT_COPY[lang];
  return (
    <div className="rounded-ds-md bg-cream-100 px-4 py-3">
      <span className="block font-display text-[15px] font-bold text-brown-700">{copy.email}</span>
      <span className="mt-1 block text-[17px] text-brown-700" dir="ltr">{email}</span>
      <span className="mt-1 block text-[13px] text-brown-500">
        {copy.lockedEmailHint}
      </span>
    </div>
  );
}

function SmartLocalForm({
  email,
  insideInstructions,
  lang,
  onDone,
}: {
  email: string;
  insideInstructions: any;
  lang: Lang;
  onDone: () => void | Promise<void>;
}) {
  const copy = SMART_CHECKOUT_COPY[lang];
  const [fullName, setFullName] = useState("");
  const { country, national, setCountry, setNational } = useProfilePhone();
  const dial = useDial(country);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const getUploadUrl = useServerFn(getBousalaReceiptUploadUrl);
  const submit = useServerFn(submitSmartReceiptPurchase);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    if (!fullName.trim()) {
      toast.error(copy.allRequired);
      return;
    }
    const phoneE164 = toE164(dial, national);
    if (!phoneE164) {
      toast.error(phoneErrorMessage(lang));
      return;
    }
    if (!file) {
      toast.error(copy.receiptRequired);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(copy.receiptTooLarge);
      return;
    }
    if (!/^(image\/|application\/pdf)/.test(file.type)) {
      toast.error(copy.receiptWrongType);
      return;
    }
    setLoading(true);
    try {
      const { path, token } = await getUploadUrl({ data: { filename: file.name } });
      const { error: upErr } = await supabase.storage
        .from("payment-receipts")
        .uploadToSignedUrl(path, token, file, { contentType: file.type });
      if (upErr) throw upErr;

      await submit({
        data: {
          email,
          full_name: fullName.trim(),
          phone: phoneE164,
          receipt_path: path,
          lang,
          country: "SY",
        },
      });
      toast.success(copy.receiptReceived);
      await onDone();
    } catch (err: any) {
      toast.error(userErrorMessage(err, lang, copy.receiptFailed));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      {insideInstructions?.body_md ? (
        <section>
          <PaymentStepHeading number={1}>{copy.transferAmount}</PaymentStepHeading>
          <InsideSyriaPaymentInstructions markdown={insideInstructions.body_md} title={insideInstructions.title_ar} lang={lang} />
        </section>
      ) : null}

      <section className="rounded-ds-xl border border-hairline-subtle bg-surface p-5 shadow-sm md:p-7">
        <PaymentStepHeading number={2}>{copy.uploadReceipt}</PaymentStepHeading>
        <div className="grid gap-4">
        <LockedEmail email={email} lang={lang} />
        <Field label={copy.fullName} value={fullName} onChange={setFullName} required autoComplete="name" />
        <PhoneField
          id="smart-local-phone"
          country={country}
          national={national}
          onCountryChange={setCountry}
          onNationalChange={setNational}
          label={copy.whatsapp}
          lang={lang}
          required
        />

        <label className="block">
          <span className="mb-1.5 block font-display text-[15px] font-bold text-brown-700">{copy.receiptLabel}</span>
          <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-ds-lg border border-dashed border-hairline-strong bg-cream-50 p-4">
            <Upload className="h-5 w-5 text-amber-800" strokeWidth={1.75} />
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block min-w-0 w-full text-[14px] text-brown-700 file:me-3 file:min-h-11 file:rounded-ds-pill file:border-0 file:bg-amber-100 file:px-4 file:font-display file:text-[14px] file:font-bold file:text-amber-800"
            />
          </div>
        </label>
        </div>

      <OrderTermsNote lang={lang} />
      <button
        type="submit"
        disabled={loading}
        className={"mt-5 " + `${btn.primary} min-h-14 w-full disabled:opacity-60`}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {copy.submitReceipt}
      </button>
      <p className="mt-3 text-center text-[13px] text-brown-500">
        {copy.receiptFooter}
      </p>
      </section>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
  placeholder,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-display text-[15px] font-bold text-brown-700">{label}</span>
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        className={DS_INPUT}
      />
    </label>
  );
}


/**
 * WhatsApp number for the checkout forms, pre-filled from the signed-in
 * profile so a returning buyer does not retype it.
 */
function useProfilePhone() {
  const [country, setCountry] = useState("SY");
  const [national, setNational] = useState("");
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(async ({ data }) => {
      const uid = data.user?.id;
      if (!uid || cancelled) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("phone_whatsapp")
        .eq("user_id", uid)
        .maybeSingle();
      if (cancelled || touched || !profile?.phone_whatsapp) return;
      const pre = prefillPhone(profile.phone_whatsapp);
      setCountry(pre.code);
      setNational(pre.national);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    country,
    national,
    setCountry: (v: string) => {
      setTouched(true);
      setCountry(v);
    },
    setNational: (v: string) => {
      setTouched(true);
      setNational(v);
    },
  };
}

/**
 * First step for a buyer without an email on their session. `updateUser` attaches the
 * email to the SAME auth user, so the result stays theirs — writing only
 * `contact_email` would leave the session anonymous and loop back here.
 */
function AddEmailStep({ needsAnonSession, lang }: { needsAnonSession: boolean; lang: Lang }) {
  const copy = SMART_CHECKOUT_COPY[lang];
  const [value, setValue] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [takenEmail, setTakenEmail] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(async ({ data }) => {
      const uid = data.user?.id;
      if (!uid || cancelled) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("contact_email")
        .eq("user_id", uid)
        .maybeSingle();
      if (!cancelled && profile?.contact_email) setValue(profile.contact_email);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function send(e?: React.FormEvent) {
    e?.preventDefault();
    const email = value.trim().toLowerCase();
    if (!email || busy) return;
    setBusy(true);
    setTakenEmail(false);
    try {
      // Created only on submit, so bots browsing the page never create users.
      if (needsAnonSession) await ensureAnonSession();
      const { error } = await supabase.auth.updateUser(
        { email },
        { emailRedirectTo: `${window.location.origin}/bousala/smart?lang=${lang}` },
      );
      if (error) {
        const msg = (error.message || "").toLowerCase();
        if (msg.includes("already") || msg.includes("registered") || msg.includes("exists")) {
          setTakenEmail(true);
          return;
        }
        throw error;
      }
      setSent(email);
    } catch (err: any) {
      toast.error(userErrorMessage(err, lang, copy.confirmationFailed));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <Card>
        <p className="font-display text-[23px] font-extrabold text-brown-700">{copy.checkEmailTitle}</p>
        <p className="mt-2 text-[17px] leading-[1.85] text-brown-700">
          {copy.checkEmailBefore} <span className="font-bold" dir="ltr">{sent}</span>. {copy.checkEmailAfter}
        </p>
        <button
          type="button"
          onClick={() => send()}
          disabled={busy}
          className="mt-5 inline-flex min-h-12 items-center gap-2 rounded-ds-pill border border-hairline-default px-5 font-display text-[15px] font-bold text-brown-700 hover:bg-cream-100 disabled:opacity-50"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {copy.resendConfirmation}
        </button>
      </Card>
    );
  }

  return (
    <form onSubmit={send} className="space-y-4 rounded-ds-xl border border-hairline-subtle bg-surface p-6 shadow-sm md:p-8">
      <h2 className="font-display text-[23px] font-extrabold text-brown-700">{copy.addEmailTitle}</h2>
      <p className="text-[17px] leading-[1.85] text-brown-700">
        {copy.addEmailBody}
      </p>
      <Field label={copy.email} value={value} onChange={setValue} type="email" required autoComplete="email" />
      {takenEmail ? (
        <p className="text-[14px] font-bold text-coral-700">
          {copy.emailTakenBefore}{" "}
          <Link
            to="/auth"
            search={{ mode: "sign_in", redirect: `/bousala/smart?lang=${lang}` } as any}
            className="underline"
          >
            {copy.signIn}
          </Link>{" "}
          {copy.emailTakenAfter}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={busy}
        className={`${btn.primary} min-h-14 w-full disabled:opacity-60`}
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        {copy.sendConfirmation}
      </button>
    </form>
  );
}
